import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../integrations/supabase/client';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

interface UserProfile {
  id: string;
  email: string;
  created_at: string;
  updated_at: string | null;
}

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        throw error;
      }

      setProfile(data);
    } catch (error) {
      toast.error('Error loading profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${profile?.email || 'User'}`} />
              <AvatarFallback>{profile?.email?.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold">{t('profile.title')}</h2>
              <p className="text-sm text-gray-500">{t('profile.memberSince')} {new Date(profile?.created_at || '').toLocaleDateString()}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium text-gray-500">{t('profile.email')}</label>
              <p className="text-lg">{profile?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('profile.lastUpdated')}</label>
              <p className="text-lg">
                {profile?.updated_at 
                  ? new Date(profile.updated_at).toLocaleDateString()
                  : t('profile.never')}
              </p>
            </div>
            
            {/* Language Preferences Section */}
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-2">Language Preferences</label>
              <div className="w-fit">
                <LanguageSwitcher />
              </div>
            </div>

            <div className="pt-4">
              <Button
                variant="outline"
                onClick={() => supabase.auth.signOut().then(() => navigate('/auth'))}
              >
                {t('profile.signOut')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 